import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatApiError, API, mrApi, mrSession } from "@/lib/apiClient";
import { formatIST } from "@/lib/utils";
import { fileToResizedDataUrl } from "@/lib/photo";
import { usePresence } from "@/lib/usePresence";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { STANDARD_PLEDGES } from "@/lib/constants";
import { toast } from "sonner";
import { ArrowLeft, Camera, X, CheckCircle2, Download, Loader2, HeartPulse } from "lucide-react";

// MR self (team) pledge: photo is MANDATORY, and these pledges are kept out
// of the official record — they appear on /live-team-pledge instead.
export default function MrSelfPledgePage() {
  const navigate = useNavigate();
  const [mr, setMr] = useState(mrSession.info());
  const [existing, setExisting] = useState(undefined); // undefined = loading
  const [photo, setPhoto] = useState(null);
  const [pledgeChoice, setPledgeChoice] = useState(STANDARD_PLEDGES[0].id);
  const [customPledge, setCustomPledge] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  usePresence("self-pledge");

  useEffect(() => {
    if (!mrSession.token()) { navigate("/mr/login"); return; }
    mrApi.get("/mr/me")
      .then((r) => { setMr(r.data); mrSession.update(r.data); })
      .catch(() => { mrSession.clear(); navigate("/mr/login"); });
    mrApi.get("/mr/self-pledge")
      .then((r) => setExisting(r.data))
      .catch(() => setExisting(null));
  }, [navigate]);

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setPhoto(await fileToResizedDataUrl(file));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const chosenPledgeText =
    pledgeChoice === "custom"
      ? customPledge.trim()
      : STANDARD_PLEDGES.find((p) => p.id === pledgeChoice)?.text || "";

  const submit = async () => {
    if (!photo) { toast.error("Your photo is required for the team pledge."); return; }
    if (pledgeChoice === "custom" && customPledge.trim().length < 20) {
      toast.error("Please write your pledge (at least 20 characters).");
      return;
    }
    if (!agreed) { toast.error("Please confirm the pledge to continue."); return; }
    setSubmitting(true);
    try {
      const res = await mrApi.post("/mr/self-pledge", { photo, pledge_text: chosenPledgeText });
      setExisting(res.data);
      toast.success(res.data.already ? "You have already pledged!" : "Team pledge recorded!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (err.response?.status === 401) { mrSession.clear(); navigate("/mr/login"); return; }
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App bg-slate-50 min-h-screen">
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between gap-3 mb-6">
          <Link to="/pledge" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0056D2]" data-testid="self-pledge-back">
            <ArrowLeft className="w-4 h-4" /> Back to pledge form
          </Link>
          {mr && <span className="text-sm text-slate-500">{mr.name} · MR {mr.emp_code}</span>}
        </div>

        {existing === undefined ? (
          <p className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin inline text-slate-400" /></p>
        ) : existing ? (
          <div data-testid="self-pledge-done" className="rounded-3xl bg-white border border-slate-200 p-8 text-center shadow-sm">
            <div className="grid place-items-center w-16 h-16 rounded-full bg-green-50 mx-auto mb-5">
              <CheckCircle2 className="w-9 h-9 text-[#138808]" />
            </div>
            <h1 className="font-display font-extrabold text-2xl text-slate-900">
              {existing.name} — you are on the team wall!
            </h1>
            <p className="mt-2 text-slate-600">Your team pledge is recorded{existing.created_at ? ` on ${formatIST(existing.created_at)}` : ""}. It is shown on the team wall (separate from the official record).</p>
            <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500">Certificate ID</p>
              <p data-testid="certificate-id" className="font-display font-bold text-xl text-[#0056D2]">{existing.certificate_id}</p>
            </div>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a href={`${API}/certificate/${existing.certificate_id}`} data-testid="self-cert-btn">
                <Button className="rounded-full bg-[#0056D2] hover:bg-blue-700 text-white font-semibold px-6">
                  <Download className="w-4 h-4 mr-2" /> Download Certificate
                </Button>
              </a>
              <Link to="/live-team-pledge" data-testid="team-wall-link">
                <Button variant="outline" className="rounded-full border-slate-300 px-6">
                  <HeartPulse className="w-4 h-4 mr-2 text-[#FF9933]" /> View Team Wall
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div data-testid="self-pledge-form" className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8">
            <h1 className="font-display font-extrabold text-3xl text-slate-900">Take Your Team Pledge</h1>
            <p className="mt-2 text-slate-500">
              Pledging as <span className="font-semibold text-slate-700">{mr?.name}</span>. Your entry appears on the
              <span className="font-semibold"> team wall</span> and is not counted in the official record.
            </p>

            <div className="mt-6">
              <p className="text-sm font-medium text-slate-700">Your Photo <span className="text-red-500">*</span> <span className="text-slate-400 font-normal">(mandatory — shown on the team wall)</span></p>
              <div className="mt-2 flex items-center gap-4">
                {photo ? (
                  <div className="relative">
                    <img src={photo} alt="Preview" data-testid="pledge-photo-preview" className="w-24 h-24 rounded-full object-cover ring-2 ring-[#FF9933]" />
                    <button type="button" data-testid="pledge-photo-remove" onClick={() => setPhoto(null)} aria-label="Remove photo"
                      className="absolute -top-1 -right-1 grid place-items-center w-6 h-6 rounded-full bg-red-500 text-white shadow">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <label className="inline-flex items-center gap-2 rounded-full bg-[#0056D2] text-white text-sm font-semibold px-5 py-2.5 cursor-pointer hover:bg-blue-700 transition-colors">
                      <Camera className="w-4 h-4" /> Take Photo
                      <input data-testid="pledge-photo-capture" type="file" accept="image/*" capture="user" className="sr-only" onChange={onPhotoChange} />
                    </label>
                    <label className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-slate-600 text-sm font-semibold px-5 py-2.5 cursor-pointer hover:border-[#0056D2] hover:text-[#0056D2] transition-colors">
                      🖼️ Upload from Gallery
                      <input data-testid="pledge-photo-input" type="file" accept="image/*" className="sr-only" onChange={onPhotoChange} />
                    </label>
                  </div>
                )}
                <p className="text-xs text-slate-500 max-w-[220px]">A clear front-facing photo works best — it appears on your card in the team wall map.</p>
              </div>
            </div>

            <div className="mt-6 rounded-2xl bg-slate-950 text-white p-5">
              <h2 className="font-display font-bold text-lg">Choose Your Pledge</h2>
              <div className="mt-3 space-y-2.5" data-testid="pledge-options">
                {STANDARD_PLEDGES.map((p) => (
                  <button key={p.id} type="button" data-testid={`pledge-option-${p.id}`} onClick={() => setPledgeChoice(p.id)}
                    className={`w-full text-left rounded-xl border p-3 transition ${pledgeChoice === p.id ? "border-[#FF9933] bg-[#FF9933]/10" : "border-white/15 bg-white/5 hover:border-white/40"}`}>
                    <p className={`text-xs font-bold uppercase tracking-wide ${pledgeChoice === p.id ? "text-[#FF9933]" : "text-slate-400"}`}>{p.title}</p>
                    <p className="mt-1 text-[13px] text-slate-200 leading-snug italic">“{p.text}”</p>
                  </button>
                ))}
                <button type="button" data-testid="pledge-option-custom" onClick={() => setPledgeChoice("custom")}
                  className={`w-full text-left rounded-xl border p-3 transition ${pledgeChoice === "custom" ? "border-[#FF9933] bg-[#FF9933]/10" : "border-white/15 bg-white/5 hover:border-white/40"}`}>
                  <p className={`text-xs font-bold uppercase tracking-wide ${pledgeChoice === "custom" ? "text-[#FF9933]" : "text-slate-400"}`}>✍️ Write my own pledge</p>
                  {pledgeChoice === "custom" && (
                    <Textarea data-testid="custom-pledge-input" value={customPledge} onChange={(e) => setCustomPledge(e.target.value)}
                      onClick={(e) => e.stopPropagation()} maxLength={1000} rows={4}
                      placeholder="Type your pledge…" className="mt-2 bg-white/10 border-white/20 text-white placeholder:text-slate-500 text-sm" />
                  )}
                </button>
              </div>
              <label className="mt-4 flex items-start gap-3 cursor-pointer">
                <Checkbox data-testid="pledge-agree-checkbox" checked={agreed} onCheckedChange={setAgreed} className="mt-0.5 border-white/40 data-[state=checked]:bg-[#FF9933] data-[state=checked]:border-[#FF9933]" />
                <span className="text-sm text-slate-200">I take this pledge as a member of the BlueCross Lifesciences team.</span>
              </label>
              <Button data-testid="submit-pledge-button" onClick={submit} disabled={submitting} size="lg"
                className="mt-4 w-full rounded-full bg-[#FF9933] hover:bg-orange-500 text-slate-900 font-bold h-12">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : "I Pledge"}
              </Button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
